/** Duration must match the CSS transition on .essay-grid */
const FILTER_FADE_MS = 180;

class EssayFilters extends HTMLElement {
  connectedCallback() {
    this._bindDesktopFilters();
    this._bindMobileFilters();
    this._bindMobileTrigger();

    document.addEventListener('click', (e) => {
      if (e.target instanceof Node && !this.contains(e.target)) this._setDropdown(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._setDropdown(false);
    });
  }

  /** @returns {NodeListOf<HTMLElement>} */
  _allCards() {
    return document.querySelectorAll('.essay-card[data-essay-tags]');
  }

  _bindDesktopFilters() {
    this.querySelectorAll('.essay-filters__desktop .essay-filters__tag').forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      btn.addEventListener('click', () =>
        this._applyFilter(btn.dataset.filter ?? '', btn.textContent?.trim() ?? '')
      );
    });
  }

  _bindMobileFilters() {
    this.querySelectorAll('.essay-filters__mobile-dropdown .essay-filters__tag').forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      btn.addEventListener('click', () =>
        this._applyFilter(btn.dataset.filter ?? '', btn.textContent?.trim() ?? '')
      );
    });
  }

  _bindMobileTrigger() {
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('.essay-filters__mobile-trigger'));
    if (!trigger) return;
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      this._setDropdown(!isOpen);
    });
  }

  /** @param {boolean} open */
  _setDropdown(open) {
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('.essay-filters__mobile-trigger'));
    const dropdown = /** @type {HTMLElement | null} */ (this.querySelector('.essay-filters__mobile-dropdown'));
    if (trigger) trigger.setAttribute('aria-expanded', String(open));
    if (dropdown) dropdown.hidden = !open;
  }

  /**
   * @param {string} filterValue
   * @param {string} filterName
   */
  _applyFilter(filterValue, filterName) {
    // Sync active state on all filter buttons immediately (no wait)
    this.querySelectorAll('.essay-filters__tag').forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      const isActive = btn.dataset.filter === filterValue;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
      if (btn.getAttribute('role') === 'option') {
        btn.setAttribute('aria-selected', String(isActive));
      }
    });

    // Update mobile trigger label
    const label = /** @type {HTMLElement | null} */ (this.querySelector('.essay-filters__active-name'));
    if (label) label.textContent = filterName;

    this._setDropdown(false);

    const grid = /** @type {HTMLElement | null} */ (document.querySelector('.essay-grid'));
    const cards = /** @type {HTMLElement[]} */ ([...this._allCards()]);

    if (!grid) {
      this._updateCards(cards, filterValue);
      return;
    }

    // Phase 1 — fade grid out
    grid.classList.add('essay-grid--filtering');

    // Phase 2 — while invisible: re-index + show/hide
    // Phase 3 — fade back in by removing the class
    // Using setTimeout matched to CSS transition duration as the reliable trigger.
    // transitionend is skipped here because prefers-reduced-motion may set duration=0
    // and some browsers fire transitionend late under load.
    setTimeout(() => {
      this._updateCards(cards, filterValue);
      // Force a reflow so the browser registers the state change before fading in
      void grid.offsetWidth;
      grid.classList.remove('essay-grid--filtering');
    }, FILTER_FADE_MS);
  }

  /**
   * Show/hide cards and re-assign standard/tall classes.
   * @param {HTMLElement[]} cards
   * @param {string} filterValue
   */
  _updateCards(cards, filterValue) {
    if (!filterValue) {
      // Restore all cards with original Liquid-assigned variant
      cards.forEach((card) => {
        card.hidden = false;
        const orig = card.dataset.essayOriginalVariant ?? 'standard';
        card.classList.toggle('essay-card--standard', orig === 'standard');
        card.classList.toggle('essay-card--tall', orig === 'tall');
      });
    } else {
      cards.forEach((card) => {
        const tags = (card.dataset.essayTags ?? '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
        card.hidden = !tags.includes(filterValue);
      });
      this._reindexVisibleCards();
    }
  }

  // Re-assigns standard/tall classes to visible cards so the alternating
  // 3-up / 2-up grid pattern is preserved regardless of which cards are shown.
  _reindexVisibleCards() {
    const visible = /** @type {HTMLElement[]} */ ([...this._allCards()].filter((c) => !c.hidden));
    visible.forEach((card, i) => {
      const isStandard = i % 5 <= 2;
      card.classList.toggle('essay-card--standard', isStandard);
      card.classList.toggle('essay-card--tall', !isStandard);
    });
  }
}

if (!customElements.get('essay-filters')) {
  customElements.define('essay-filters', EssayFilters);
}
